// The postMessage contract between the Chrome extension's side panel and this framed page (W8).
//
// The side panel (`scout-extension/src/sidepanel.js`) is a `chrome-extension://` page holding an
// <iframe> of `/embed/extension`. It reads the open tab on a click and posts what it read here;
// this page posts back its sign-in state, asks for a re-read, and hands over a Gmail draft to
// insert. Both ends check origin and source: a message from anywhere else is ignored.
//
// Pure functions only, so `node --test` covers them without a DOM.

import type { PageKind } from '../api/types.ts';

// The extension's id is pinned by `manifest.key` in `scout-extension/manifest.template.json`,
// so its origin is fixed. A second build with a different key would need adding here.
export const EXTENSION_ORIGINS: readonly string[] = ['chrome-extension://daemkhakjbbmomopcfjnnlnkjkncgpke'];

// Caps on what the page may hand us. The server caps again (TurnView, draft_reply).
const SHORT_TEXT_CHARACTERS = 300;
const LONG_TEXT_CHARACTERS = 20000;

export const PAGE_KINDS: readonly PageKind[] = [
  'linkedin_profile', 'linkedin_company', 'sales_nav_lead', 'sales_nav_company', 'gmail_thread', 'website',
];

/** What the extension read off the open tab. Every string is page text: untrusted. */
export interface PageContext {
  kind: PageKind;
  url: string;
  title: string;
  host: string;
  // False when the reader recognised the page but could not parse it: show "type the name".
  read_ok: boolean;
  // A tool or app the team works in (Amplemarket, Salesforce, Docs), flagged by the extension's
  // one list in `scout-extension/src/domain.js`: not a company, so nothing to look up.
  tool_site?: boolean;
  site_name?: string;
  description?: string;
  person_name?: string;
  person_title?: string;
  person_company?: string;
  // The person's current company as its LinkedIn page, when the profile links it.
  person_company_url?: string;
  profile_url?: string;
  company_name?: string;
  company_website?: string;
  // A LinkedIn company page's own canonical address.
  company_page_url?: string;
  email_subject?: string;
  email_from_name?: string;
  email_from_address?: string;
  email_body?: string;
  selection?: string;
  compose_draft?: string;
}

// `checking`: the frame is still asking the extension for a token (the shell waits, no fallback).
export type EmbedState = 'loading' | 'checking' | 'signed_out' | 'signed_in' | 'denied';

/** Messages this page posts to the side panel. */
export type EmbedMessage =
  | { type: 'scout:embed-status'; state: EmbedState; email?: string | null }
  | { type: 'scout:read-page' }
  | { type: 'scout:insert-reply'; text: string };

export function isTrustedExtensionOrigin(origin: string, allowed: readonly string[] = EXTENSION_ORIGINS): boolean {
  return allowed.includes(origin);
}

// LinkedIn's labels lead with an action ("View company: CallRail", "Current company: …",
// "Company logo for …"), which is not part of the name. The extension strips them too; this
// covers a build that does not yet.
export function cleanCompanyLabel(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const cleaned = value
    .replace(/^\s*(?:view company|current company|company)\s*:\s*/i, '')
    .replace(/^\s*company logo for\s+/i, '')
    .replace(/\.\s*Click.*$/i, '')
    .replace(/\s+logo$/i, '')
    .trim();
  return cleaned || undefined;
}

function text(value: unknown, limit: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, limit);
}

/** The context from a `scout:page-context` message, or null for anything malformed. */
export function parsePageContext(raw: unknown): PageContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const message = raw as { type?: unknown; context?: unknown };
  if (message.type !== 'scout:page-context' || !message.context || typeof message.context !== 'object') return null;
  const context = message.context as Record<string, unknown>;
  const kind = context.kind;
  if (typeof kind !== 'string' || !PAGE_KINDS.includes(kind as PageKind)) return null;

  // Short fields are names and titles; the long ones are an email body, a selection, a draft.
  return {
    kind: kind as PageKind,
    url: text(context.url, 500) ?? '',
    title: text(context.title, SHORT_TEXT_CHARACTERS) ?? '',
    host: text(context.host, 253) ?? '',
    read_ok: context.read_ok === true,
    tool_site: context.tool_site === true ? true : undefined,
    site_name: text(context.site_name, SHORT_TEXT_CHARACTERS),
    description: text(context.description, SHORT_TEXT_CHARACTERS),
    person_name: text(context.person_name, 200),
    person_title: text(context.person_title, SHORT_TEXT_CHARACTERS),
    person_company: cleanCompanyLabel(text(context.person_company, 200)),
    person_company_url: text(context.person_company_url, 500),
    profile_url: text(context.profile_url, 500),
    company_name: cleanCompanyLabel(text(context.company_name, 200)),
    company_website: text(context.company_website, 500),
    company_page_url: text(context.company_page_url, 500),
    email_subject: text(context.email_subject, SHORT_TEXT_CHARACTERS),
    email_from_name: text(context.email_from_name, 200),
    email_from_address: text(context.email_from_address, 320),
    email_body: text(context.email_body, LONG_TEXT_CHARACTERS),
    selection: text(context.selection, LONG_TEXT_CHARACTERS),
    compose_draft: text(context.compose_draft, LONG_TEXT_CHARACTERS),
  };
}

/** The shape of a `message` event the bridge reads. A real MessageEvent fits it. */
export interface BridgeEvent {
  origin: string;
  source: unknown;
  data: unknown;
}

/** True only for a message from the pinned extension origin *and* from the window framing us. */
export function isFromExtensionParent(event: BridgeEvent, parentWindow: unknown): boolean {
  return isTrustedExtensionOrigin(event.origin) && event.source === parentWindow;
}

/** The page context in a message, or null for a wrong origin, a wrong source or junk. */
export function pageContextFromEvent(event: BridgeEvent, parentWindow: unknown): PageContext | null {
  if (!isFromExtensionParent(event, parentWindow)) return null;
  return parsePageContext(event.data);
}

/** The side panel's answer to an Insert, or null when the message is not one from it. */
export function insertResultFromEvent(event: BridgeEvent, parentWindow: unknown): { ok: boolean; reason: string | null } | null {
  if (!isFromExtensionParent(event, parentWindow)) return null;
  const data = event.data as { type?: unknown; ok?: unknown; reason?: unknown } | null;
  if (!data || data.type !== 'scout:insert-result') return null;
  return { ok: data.ok === true, reason: typeof data.reason === 'string' ? data.reason : null };
}

/** True when this window is framed by something (the side panel). */
export function isFramed(): boolean {
  try {
    return globalThis.window !== undefined && window.parent !== window;
  } catch {
    return true;
  }
}

/** Post to the side panel. A mismatched target origin drops the message, which is the point. */
export function postToExtension(message: EmbedMessage): void {
  if (!isFramed()) return;
  for (const origin of EXTENSION_ORIGINS) {
    window.parent.postMessage(message, origin);
  }
}
